import type { FocusTrap } from "focus-trap";
import type { Config as OverlayConfig } from "utils/overlay";
import fastdom from "fastdom";
import { createFocusTrap } from "focus-trap";

import { hide as hideOverlay, show as showOverlay } from "utils/overlay";
import repaint from "utils/repaint";
import transitionEnd from "utils/transition-end";

type Events = "open" | "opened" | "close" | "closed";

type CloseConfig = {
  escape: boolean;
  clickOutside: boolean;
};

type Config = {
  close: CloseConfig | false;
  overlay: OverlayConfig | false;
};

export type ConfigConstructor = Partial<{
  close: boolean | Partial<CloseConfig>;
  overlay: boolean | Partial<OverlayConfig>;
}>;

export default class {
  public readonly root: HTMLElement;

  private isActive: boolean;

  private focusTrap?: FocusTrap;

  private readonly config: Config;

  constructor(element: HTMLElement, options?: ConfigConstructor) {
    this.root = element;
    this.config = {
      close: {
        escape: true,
        clickOutside: true,
      },
      overlay: {
        index: null,
      },
    };

    if (typeof options?.close !== "undefined") {
      if (typeof options.close === "object") {
        Object.assign(this.config.close, options.close);
      } else if (!options.close) {
        this.config.close = false;
      }
    }

    if (typeof options?.overlay !== "undefined") {
      if (typeof options?.overlay === "object") {
        Object.assign(this.config.overlay, options.overlay);
      } else if (!options.overlay) {
        this.config.overlay = false;
      }
    }

    this.isActive = false;
    this.root.hidden = true;

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.toggle = this.toggle.bind(this);

    this.handleEvents();
  }

  protected getActiveClass(): string {
    throw new Error("There must exist an `getActiveClass` method in final class");
  }

  private handleEvents(): void {
    this.on("open", () => {
      if (this.config.close) {
        if (this.config.close.clickOutside) {
          document.addEventListener("touchstart", this.clickEvent);
          document.addEventListener("click", this.clickEvent);
        }

        if (this.config.close.escape) {
          document.addEventListener("keydown", this.keydownEvent);
        }
      }
    });

    this.on("open", async () => {
      if (!this.focusTrap) {
        this.focusTrap = createFocusTrap(this.root, {
          allowOutsideClick: true,
        });
      }

      if (this.isActive) {
        this.focusTrap.activate();
      }
    });

    this.on("close", () => {
      this.focusTrap?.deactivate();

      if (this.config.close) {
        if (this.config.close.clickOutside) {
          document.removeEventListener("touchstart", this.clickEvent);
          document.removeEventListener("click", this.clickEvent);
        }

        if (this.config.close.escape) {
          document.removeEventListener("keydown", this.keydownEvent);
        }
      }
    });
  }

  private clickEvent(event: MouseEvent | TouchEvent): void {
    if (!(event.target instanceof Node)) {
      return;
    }
    
    if ((event.target as HTMLElement).closest('#cookiescript_injected_wrapper')){
      return;
    }

    if (!this.root.contains(event.target)) {
      event.stopPropagation();

      this.close();
    }
  }

  private keydownEvent(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close();
    }
  }

  private dispatchEvent(type: Events): void {
    const event = new Event(type);

    this.root.dispatchEvent(event);
  }

  toggle(): void {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (this.isActive) {
      return;
    }

    this.isActive = true;

    fastdom.measure(() => {
      this.root.hidden = false;
      repaint(this.root);
    });

    fastdom.mutate(() => {
      this.dispatchEvent("open");
      this.root.classList.add(this.getActiveClass());

      transitionEnd(this.root, () => {
        if (this.isActive) {
          this.dispatchEvent("opened");
        }
      });
    });

    if (this.config.overlay) {
      showOverlay(this.config.overlay);
    }
  }

  close(): void {
    if (!this.isActive) {
      return;
    }

    this.isActive = false;

    fastdom.mutate(() => {
      this.dispatchEvent("close");
      this.root.classList.remove(this.getActiveClass());

      transitionEnd(this.root, () => {
        if (!this.isActive) {
          this.root.hidden = true;
          this.dispatchEvent("closed");
        }
      });
    });

    if (this.config.overlay) {
      hideOverlay();
    }
  }

  on(
    type: Events,
    callback: CallableFunction,
    options?: boolean | AddEventListenerOptions
  ): void {
    this.root.addEventListener(type, (event) => callback(event), options);
  }
}
